// zod schemas are referenced indirectly via validators registry
import { LICENSES_BASE_ROUTE, LICENSES_BASE_SLUGS } from '@koda/koda-utils/constants/licenses';
import { stegaClean } from 'next-sanity';
import { DA, MainLocale } from '../../../../i18n/constants';
import { licenseSlugs, StepRoute } from '../../../../i18n/pathnames/licenses';
import { LicenseError } from '../../../../src/lib/errors';
import { getFlow, LicenseFlowType, LicenseFlowTypeValue, Step } from './flows';
import type { SanityLicense } from './types/licenses';

export class FlowController {
  private licenseType: string;
  private steps: Step[] | undefined;
  public flowType: LicenseFlowTypeValue | undefined;
  public countedSteps: Step[] | undefined;

  constructor(license: SanityLicense) {
    if (!license) {
      throw new LicenseError('No license');
    }

    this.licenseType = license.slug?.current || '<unknown>';
    if (license.flowType && this.isLicenseFlowType(license.flowType) && license.licenseId) {
      this.flowType = license.flowType;
      this.steps = getFlow(license);
      this.countedSteps = this.steps.filter(step => !step.removeFromProgress);
    } else {
      throw new Error('Invalid license flow type');
    }
  }

  private isLicenseFlowType(flow: string): flow is LicenseFlowTypeValue {
    return (
      flow === LicenseFlowType.A ||
      flow === LicenseFlowType.B ||
      flow === LicenseFlowType.C ||
      flow === LicenseFlowType.D
    );
  }

  private normalizeRootSlug(slug: string) {
    return slug === '' ? '/' : slug;
  }

  getNextRouteStep(currentStepRoute: StepRoute, direction: number) {
    const currentIndex = this.steps?.findIndex(step => step.stepRoute === currentStepRoute);
    const nextIndex = currentIndex !== undefined ? currentIndex + direction : -1;
    return this.steps?.[nextIndex];
  }

  getAbsoluteRouteInDirection({
    currentStepRoute,
    direction,
    locale,
  }: {
    currentStepRoute: StepRoute;
    direction: number;
    locale: MainLocale;
  }) {
    const nextStep = this.getNextRouteStep(currentStepRoute, direction);
    if (!nextStep) throw new LicenseError('No next step found');
    const localePrefix = locale === 'da' ? '' : `/${locale}`;
    if (nextStep.stepRoute === LICENSES_BASE_ROUTE) return `${localePrefix}/${LICENSES_BASE_SLUGS[locale]}`; // Go to licenses overview page
    const licenseStepSlug = licenseSlugs[nextStep.stepRoute][locale];
    return `${localePrefix}/${LICENSES_BASE_SLUGS[locale]}/${stegaClean(this.licenseType)}/${licenseStepSlug}`;
  }

  getProgressFromSlug(currentStepRoute: StepRoute) {
    if (!this.countedSteps || !currentStepRoute) return null;

    const normalized = this.normalizeRootSlug(currentStepRoute);
    const index = this.countedSteps.findIndex(s => this.normalizeRootSlug(s.stepRoute) === normalized);
    if (index === -1) return null;

    const total = this.countedSteps?.length ?? 0;
    const step = this.countedSteps[index];
    return {
      currentStepIndex: index, // zero-based
      currentStepNumber: index + 1, // one-based
      totalSteps: total,
      percent: total > 0 ? ((index + 1) / total) * 100 : 0,
      step,
    };
  }

  getCurrentStep(currentStepRoute: StepRoute) {
    if (!this.steps || !currentStepRoute) return undefined;
    const normalized = this.normalizeRootSlug(currentStepRoute);
    return stegaClean(this.steps.find(s => this.normalizeRootSlug(s.stepRoute) === normalized));
  }

  // New: expose steps (read-only copy) for external hooks without breaking encapsulation
  getSteps(): Step[] {
    return this.steps ? [...this.steps] : [];
  }

  getCountedSteps(): Step[] {
    return this.countedSteps ? [...this.countedSteps] : [];
  }

  getLicenseType() {
    return this.licenseType;
  }

  getAbsoluteRouteForStep(stepRoute: StepRoute, locale?: MainLocale) {
    const step = this.steps?.find(s => s.stepRoute === stepRoute);
    if (!step) return undefined;

    // Backwards-compatible fallback (internal route) if locale isn't provided.
    // Prefer passing locale to get a public, localized pathname.
    if (!locale) {
      return `/${LICENSES_BASE_ROUTE}/${stegaClean(this.licenseType)}/${step.stepRoute}`;
    }

    const localePrefix = locale === DA ? '' : `/${locale}`;
    if (step.stepRoute === LICENSES_BASE_ROUTE) return `${localePrefix}/${LICENSES_BASE_SLUGS[locale]}`;

    const licenseStepSlug = licenseSlugs[step.stepRoute][locale];
    return `${localePrefix}/${LICENSES_BASE_SLUGS[locale]}/${stegaClean(this.licenseType)}/${licenseStepSlug}`;
  }
}
